/**
 * brandProductNameMatch.js
 *
 * Fuzzy name matcher used to de-duplicate LLM-generated cast elements
 * against brand-product-sheet elements in the "Props & Elements" list.
 *
 * Rule: an LLM-generated element is considered a duplicate of a brand
 * product sheet when the sheet's DISTINCTIVE tokens are all contained
 * in the LLM element's name (or vice-versa).
 *
 * Examples (with brand-sheet name = "Flyer 14T"):
 *   • "Flyer 14T"          → duplicate (exact match)
 *   • "Flyer 14T Bicycle"  → duplicate (all brand tokens present)
 *   • "The Flyer 14T bike" → duplicate (all brand tokens present)
 *   • "Rusty Outgrown Bikes" → NOT a duplicate
 *   • "Suburban Bangalore Garage" → NOT a duplicate
 *
 * Distinctiveness: a matcher fires only if the brand-sheet tokens
 * include at least one "distinctive" token — either ≥3 characters or
 * containing a digit. This prevents a single generic word like "bike"
 * from swallowing every bike-related element in the list.
 */

const STOPWORDS = new Set([
    'a', 'an', 'the', 'and', 'or', 'of', 'to', 'for', 'in', 'on', 'at',
    'by', 'with', 'from', 'this', 'that', 'my', 'our', 'your',
]);

/**
 * Normalize a name into an array of comparison tokens.
 * - Lowercase, trim.
 * - Strip common punctuation.
 * - Split on whitespace.
 * - Drop stopwords and 1-character tokens.
 */
export function tokenizeName(name) {
    if (!name) return [];
    const cleaned = String(name)
        .toLowerCase()
        .replace(/[^a-z0-9\s]+/g, ' ')
        .trim();
    if (!cleaned) return [];
    return cleaned
        .split(/\s+/)
        .filter(t => t.length >= 2 && !STOPWORDS.has(t));
}

/**
 * Is a token "distinctive" enough to anchor a match?
 * A token with a digit (e.g. "14t") or length ≥ 3 (e.g. "flyer") is
 * distinctive; single common short words like "bt" or generic 2-letter
 * fragments are not.
 */
function isDistinctive(token) {
    if (!token) return false;
    if (/\d/.test(token)) return true;
    return token.length >= 3;
}

/**
 * Returns true if `candidateName` is a fuzzy duplicate of
 * `brandSheetName`. Order matters: the brand-sheet is the source of
 * truth, so we accept a match when either
 *   (a) every brand-sheet token appears in the candidate's tokens, OR
 *   (b) every candidate token appears in the brand-sheet's tokens
 *       AND the brand-sheet has at least one distinctive token that
 *       also appears in the candidate.
 * Case (b) covers "Flyer 14T Bike" (brand) vs "Flyer 14T" (LLM).
 */
export function isDuplicateOfBrandProduct(candidateName, brandSheetName) {
    const brandTokens     = tokenizeName(brandSheetName);
    const candidateTokens = tokenizeName(candidateName);
    if (brandTokens.length === 0 || candidateTokens.length === 0) return false;

    const brandHasDistinctive = brandTokens.some(isDistinctive);
    if (!brandHasDistinctive) return false;

    const candSet  = new Set(candidateTokens);
    const brandSet = new Set(brandTokens);

    // (a) all brand tokens present in candidate → duplicate
    const brandSubsetOfCand = brandTokens.every(t => candSet.has(t));
    if (brandSubsetOfCand) return true;

    // (b) all candidate tokens present in brand AND at least one
    //     distinctive brand token is in the candidate. Guards against
    //     "flyer" (LLM) matching "flyer 14t" (brand) when the LLM name
    //     shares only a generic token.
    const candSubsetOfBrand = candidateTokens.every(t => brandSet.has(t));
    const sharesDistinctive = brandTokens
        .filter(isDistinctive)
        .some(t => candSet.has(t));
    if (candSubsetOfBrand && sharesDistinctive) return true;

    return false;
}

/**
 * Given a candidate name, find the first matching brand-sheet name
 * from `brandSheetNames`. Returns the matched brand-sheet name or
 * `null`. Match is case-insensitive and token-based (see
 * `isDuplicateOfBrandProduct`).
 */
export function findMatchingBrandSheetName(candidateName, brandSheetNames) {
    if (!candidateName || !Array.isArray(brandSheetNames) || brandSheetNames.length === 0) {
        return null;
    }
    for (const bsn of brandSheetNames) {
        if (isDuplicateOfBrandProduct(candidateName, bsn)) return bsn;
    }
    return null;
}
